
import { Lock, Shield, UserCheck, FileCheck, AlertTriangle, Key } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const AccessControlManagement = () => {
  return (
    <LandingPageTemplate
      title="Access Control Management"
      description="Comprehensive access control management services to secure your organization's resources and data."
      keywords={[
        "access control",
        "identity management",
        "access management",
        "security controls",
        "authorization"
      ]}
      canonicalUrl="/cybersecurity/access-control-management/"
      schemaType="service"
      schemaData={{
        name: "Access Control Management Services",
        description: "Expert access control and identity management solutions",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Secure Access"
        highlighted="Control Management"
        description="Expert implementation and management of access controls to protect your organization's resources and data."
        ctaText="Get Started"
        heroPattern="roadmap"
      />

      <FeatureSection 
        title="Comprehensive Access Management"
        subtitle="KEY FEATURES"
        description="Our access control management services provide complete oversight of your organization's access security."
        features={[
          {
            title: "Identity Management",
            description: "Centralized identity and access management.",
            icon: <UserCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Controls",
            description: "Implement robust access control mechanisms.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Policy Management",
            description: "Develop and maintain access control policies.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk Assessment",
            description: "Evaluate access-related security risks.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Authentication",
            description: "Multi-factor authentication implementation.",
            icon: <Key className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Review",
            description: "Regular access rights review and certification.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Management Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to implementing and managing access controls"
        steps={[
          {
            title: "Initial Assessment",
            description: "Evaluate current access control environment"
          },
          {
            title: "Policy Development",
            description: "Create comprehensive access control policies"
          },
          {
            title: "Implementation",
            description: "Deploy access control solutions"
          },
          {
            title: "Review Process",
            description: "Establish ongoing access review procedures"
          },
          {
            title: "Monitoring",
            description: "Continuous access control monitoring"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our Access Management"
        benefits={[
          {
            title: "Security Expertise",
            description: "Work with certified security specialists"
          },
          {
            title: "Complete Solution",
            description: "End-to-end access control management"
          },
          {
            title: "Policy Framework",
            description: "Comprehensive policy development"
          },
          {
            title: "Regular Reviews",
            description: "Ongoing access certification"
          },
          {
            title: "Risk Reduction",
            description: "Minimize unauthorized access risks"
          },
          {
            title: "Compliance Support",
            description: "Meet regulatory requirements"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Secure Your Access Controls?"
        description="Schedule a consultation to discuss your access control needs."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request Access Control Services"
        modalDescription="Tell us about your organization's access control requirements."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Access Control",
          service_interest: "Security Services"
        }}
      />
    </LandingPageTemplate>
  );
};

export default AccessControlManagement;
